// Interactive study chapters. Chapter 1 reproduces the original Drop 2
// source-rotation assignment exactly (34 groups / 180 cards). Chapters 2-6 continue the
// Willmott-style curriculum (section order follows Mel Bay 95112; the drill
// packaging is an engine-buildable extrapolation):
//   2  Voice-leading every top voice (top-voice ladder, two-layout b9 check)
//   3  Tensions on top: 9ths and altered 9ths
//   4  Voice-leading chord scales (ii-V-I with a fixed lead)
//   5  Subs, diminished tension and symmetry
//   6  Song examples and reharmonization

import { CHORD_TYPES, CORE_CHORD_TYPE_IDS, chordSymbol, type ChordTypeId } from '@/music/chords';
import { topVoiceToneName, type Inversion } from '@/music/voicing';

export interface ChapterCardSpec {
  root: string;
  chordType: ChordTypeId;
  inversion: Inversion;
  caption: string;
  leadNote?: string;
}
export interface ChapterGroup {
  title: string;
  note?: string;
  cards: ChapterCardSpec[];
}

export interface ChapterSection {
  id: string;
  title: string;
  note: string;
  groups: ChapterGroup[];
}

export interface ChapterStudyChart {
  presetId: string;
  label: string;
  note: string;
  optimize?: boolean;
}

export interface Chapter {
  id: string;
  number: number;
  title: string;
  concept: string;
  sections: ChapterSection[];
  studyCharts?: ChapterStudyChart[];
}

// ---------- Chapter 1 ----------

const formula = (t: ChordTypeId) => CHORD_TYPES[t].formula;
const cSource = (t: ChordTypeId) => chordSymbol('C', t);
const topVoiceCaption = (t: ChordTypeId, rotation: Inversion) =>
  `${topVoiceToneName(t, rotation)} on top`;

const chapter1: Chapter = {
  id: 'inversions',
  number: 1,
  title: 'Top voices',
  concept:
    'Every four-part chord type as a Drop 2 voicing, with each chord tone on top. Rotating the close-position chord sets the top voice. The bass after the drop sets the inversion. The b9 voicings are included and marked.',
  sections: [
    {
      id: 'approach-1',
      title: 'Approach 1',
      note: 'All 15 core chord types: put the final listed formula tone on top in D, then the first in C, the second in B♭ and the third in G.',
      groups: [
        { root: 'D', inversion: 0 as Inversion, label: 'D — final formula tone on top' },
        { root: 'C', inversion: 1 as Inversion, label: 'C — first formula tone on top' },
        { root: 'Bb', inversion: 2 as Inversion, label: 'B♭ — second formula tone on top' },
        { root: 'G', inversion: 3 as Inversion, label: 'G — third formula tone on top' },
      ].map((g) => ({
        title: g.label,
        cards: CORE_CHORD_TYPE_IDS.map((t) => ({
          root: g.root,
          chordType: t,
          inversion: g.inversion,
          caption: `${cSource(t)}: ${formula(t)} · ${topVoiceCaption(t, g.inversion)}`,
        })),
      })),
    },
    {
      id: 'approach-2',
      title: 'Approach 2',
      note: 'In F: each chord tone on top, one chord type at a time.',
      groups: CORE_CHORD_TYPE_IDS.map((t) => ({
        title: `F${CHORD_TYPES[t].symbol} — ${formula(t)}`,
        cards: ([0, 1, 2, 3] as Inversion[]).map((inv) => ({
          root: 'F',
          chordType: t,
          inversion: inv,
          caption: topVoiceCaption(t, inv),
        })),
      })),
    },
    {
      id: 'approach-3',
      title: 'Approach 3',
      note: 'For each chord type, roots descend C–A–G♭–E♭ while the top voice moves through successive chord tones.',
      groups: CORE_CHORD_TYPE_IDS.map((t) => ({
        title: `${cSource(t)} symmetrical pattern`,
        cards: [
          { root: 'C', inversion: 1 as Inversion },
          { root: 'A', inversion: 2 as Inversion },
          { root: 'Gb', inversion: 3 as Inversion },
          { root: 'Eb', inversion: 0 as Inversion },
        ].map((step) => ({
          root: step.root,
          chordType: t,
          inversion: step.inversion,
          caption: `${step.root} · ${topVoiceCaption(t, step.inversion)}`,
        })),
      })),
    },
  ],
};

// ---------- Chapter 2 ----------

const ladderTypes: ChordTypeId[] = ['maj7', 'dom7', 'min7', 'min7b5', 'dom7b9'];

const chapter2: Chapter = {
  id: 'voice-leading-inversions',
  number: 2,
  title: 'Voice-leading the top voices',
  concept:
    'Connecting the Chapter 1 shapes. Rotating the close-position chord moves the top voice through each chord tone. The bass after the drop sets the inversion. A chord can have a b9 on one string layout and not the other.',
  sections: [
    {
      id: 'top-voice-ladder',
      title: 'Top voices in C',
      note: 'Each chord type in C with each of its four chord tones on top. The label under the top string names the top note.',
      groups: ladderTypes.map((t) => ({
        title: `C${CHORD_TYPES[t].symbol}`,
        cards: ([0, 1, 2, 3] as Inversion[]).map((inv) => ({
          root: 'C',
          chordType: t,
          inversion: inv,
          caption: topVoiceCaption(t, inv),
        })),
      })),
    },
  ],
};

// ---------- Chapter 3 ----------

const chapter3: Chapter = {
  id: 'tensions-on-top',
  number: 3,
  title: 'Ninths and altered ninths on top',
  concept:
    'In a four-note ninth chord the 9 replaces the root, so the 9 can be the top note. Each 7th chord is followed by its 9th chord, then the altered dominants (♭9, ♯9) used in minor cadences.',
  sections: [
    {
      id: 'seventh-to-ninth',
      title: '7th to 9th',
      note: 'Each quality with the 9 on top. The 9 replaces the root.',
      groups: [
        {
          title: 'Major',
          cards: [
            {
              root: 'C',
              chordType: 'maj7' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'Cmaj7',
            },
            {
              root: 'C',
              chordType: 'maj9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'Cmaj9 (9 on top)',
              leadNote: 'D',
            },
          ],
        },
        {
          title: 'Dominant',
          cards: [
            {
              root: 'C',
              chordType: 'dom7' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'C7',
            },
            {
              root: 'C',
              chordType: 'dom9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'C9 (9 on top)',
              leadNote: 'D',
            },
          ],
        },
        {
          title: 'Minor',
          cards: [
            {
              root: 'C',
              chordType: 'min7' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'Cm7',
            },
            {
              root: 'C',
              chordType: 'min9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'Cm9 (9 on top)',
              leadNote: 'D',
            },
          ],
        },
      ],
    },
    {
      id: 'altered-dominants',
      title: 'Altered dominants on G',
      note: 'G7 with each ninth on top: natural 9, ♭9, then ♯9.',
      groups: [
        {
          title: 'G dominant family',
          cards: [
            {
              root: 'G',
              chordType: 'dom9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'G9',
              leadNote: 'A',
            },
            {
              root: 'G',
              chordType: 'dom7b9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'G7♭9',
              leadNote: 'Ab',
            },
            {
              root: 'G',
              chordType: 'dom7s9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'G7♯9',
              leadNote: 'Bb',
            },
          ],
        },
      ],
    },
  ],
  studyCharts: [
    {
      presetId: 'willmott-9ths-lead-study',
      label: 'Willmott 9ths Lead Study',
      note: 'A descending line of ninth and altered-ninth chords, with the ninth on top.',
      optimize: true,
    },
  ],
};

// ---------- Chapter 4 ----------

const chapter4: Chapter = {
  id: 'voice-leading-chord-scales',
  number: 4,
  title: 'Chord scales through ii–V–I',
  concept:
    'A fixed top line with the chords changing underneath it. ii, V and I each support the same melody notes.',
  sections: [
    {
      id: 'major-iivi-9ths',
      title: 'Major ii–V–I in 9ths',
      note: 'Dm9 → G9 → Cmaj9 → C6/9, each with its extension on top.',
      groups: [
        {
          title: 'C major',
          cards: [
            {
              root: 'D',
              chordType: 'min9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'Dm9',
              leadNote: 'E',
            },
            {
              root: 'G',
              chordType: 'dom9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'G9',
              leadNote: 'A',
            },
            {
              root: 'C',
              chordType: 'maj9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'Cmaj9',
              leadNote: 'D',
            },
            {
              root: 'C',
              chordType: 'maj69' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'C6/9',
              leadNote: 'D',
            },
          ],
        },
      ],
    },
    {
      id: 'minor-iivi-tension',
      title: 'Minor ii–V–i with tensions',
      note: 'Dm7♭5 → G7♭9 → Cm9 → Cm6/9. The altered dominant resolves to the minor tonic.',
      groups: [
        {
          title: 'C minor',
          cards: [
            {
              root: 'D',
              chordType: 'min7b5' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'Dm7♭5',
              leadNote: 'F',
            },
            {
              root: 'G',
              chordType: 'dom7b9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'G7♭9',
              leadNote: 'Db',
            },
            {
              root: 'C',
              chordType: 'min9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'Cm9',
              leadNote: 'D',
            },
            {
              root: 'C',
              chordType: 'min69' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'Cm6/9',
              leadNote: 'D',
            },
          ],
        },
      ],
    },
  ],
  studyCharts: [
    {
      presetId: 'major-ii-v-i-9ths',
      label: 'Major ii-V-I 9ths',
      note: 'The major cadence in 9ths, voice-led.',
      optimize: true,
    },
    {
      presetId: 'minor-ii-v-i',
      label: 'Minor ii-V-i',
      note: 'The minor cadence in 7ths, for comparison with the 9ths version.',
      optimize: true,
    },
  ],
};

// ---------- Chapter 5 ----------

const chapter5: Chapter = {
  id: 'subs-symmetry',
  number: 5,
  title: 'Substitutes and diminished symmetry',
  concept:
    'The tritone substitute replaces a dominant with the dominant a tritone away. A diminished 7th repeats every minor third. A half-diminished chord has the same notes as a minor 6th chord a minor third higher.',
  sections: [
    {
      id: 'tritone-sub',
      title: 'Tritone substitution',
      note: 'G9 and D♭9 share their guide tones and both resolve to Cmaj9.',
      groups: [
        {
          title: 'V and its tritone sub → I',
          cards: [
            {
              root: 'G',
              chordType: 'dom9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'G9 (V)',
              leadNote: 'A',
            },
            {
              root: 'Db',
              chordType: 'dom9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'D♭9 (subV)',
              leadNote: 'Eb',
            },
            {
              root: 'C',
              chordType: 'maj9' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'Cmaj9 (I)',
              leadNote: 'D',
            },
          ],
        },
      ],
    },
    {
      id: 'dim-symmetry',
      title: 'Diminished symmetry',
      note: 'The diminished 7th shape repeats every minor third: C°7 = E♭°7 = G♭°7 = A°7.',
      groups: [
        {
          title: 'One diminished chord, four names',
          cards: [
            {
              root: 'C',
              chordType: 'dim7' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'C°7',
            },
            {
              root: 'Eb',
              chordType: 'dim7' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'E♭°7',
            },
            {
              root: 'Gb',
              chordType: 'dim7' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'G♭°7',
            },
            {
              root: 'A',
              chordType: 'dim7' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'A°7',
            },
          ],
        },
      ],
    },
    {
      id: 'halfdim-equals-m6',
      title: 'Half-diminished and minor 6th',
      note: 'An m7♭5 chord has the same four notes as the minor 6th chord a minor third higher: Am7♭5 = Cm6.',
      groups: [
        {
          title: 'Two readings of one voicing',
          cards: [
            {
              root: 'A',
              chordType: 'min7b5' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'Am7♭5',
            },
            {
              root: 'C',
              chordType: 'min6' as ChordTypeId,
              inversion: 0 as Inversion,
              caption: 'Cm6',
            },
          ],
        },
      ],
    },
  ],
};

// ---------- Chapter 6 ----------

const chapter6: Chapter = {
  id: 'song-examples',
  number: 6,
  title: 'Tunes and reharmonisation',
  concept:
    'Whole tunes. Voice each form with a guide-tone melody, then reharmonise with the ninths, substitutes and diminished chords from Chapters 3–5. Each chart opens in the workbench.',
  sections: [],
  studyCharts: [
    {
      presetId: 'days-style-lead-note-study',
      label: 'Days-Style Lead Note Study',
      note: 'A 32-bar study with a written top voice in every bar.',
      optimize: true,
    },
    {
      presetId: 'blues-for-alice',
      label: 'Blues for Alice',
      note: 'A Bird blues with many ii–Vs. Reharmonise bars with ninths and a passing diminished chord.',
      optimize: true,
    },
    {
      presetId: 'autumn-leaves-study',
      label: 'Autumn Leaves Study',
      note: 'Voice the changes in 7ths, then in 9ths.',
      optimize: true,
    },
  ],
};

export const CHAPTERS: Chapter[] = [chapter1, chapter2, chapter3, chapter4, chapter5, chapter6];

export function getChapter(id: string | undefined): Chapter {
  return CHAPTERS.find((c) => c.id === id) ?? CHAPTERS[0];
}
